import * as Sentry from "@sentry/react";
import type {
	AdapterStatus,
	AudioPlaybackService,
	PlaybackStatus,
	SingleAudioPlaybackServiceArgs,
} from "../../application/ports";
import type { PlaybackStore } from "../../application/stores/playbackStore";
import type { TrackModel } from "@coffitivity/shared";

// audio/AudioElementController.ts
export class WebAudioAdapter implements AudioPlaybackService {
	// public properties
	public data: TrackModel;
	public preferredMimeType: "mp3" | "ogg";
	public usesAudioContext = false;
	public isSubTrack: boolean;
	public onPlay: (() => void) | null = null;
	public onPause: (() => void) | null = null;
	public onEnded: (() => void) | null = null;

	// private properties
	private audio: HTMLAudioElement;
	private store: PlaybackStore;
	private _url: string;
	private isLooping: boolean;
	private _isPlaying = false;
	private _masterVolume: number;
	private _trackVolume: number;
	private updateAdapterState: (
		trackId: string,
		playbackStatus?: PlaybackStatus,
		volume?: number,
	) => void;

	constructor(_args: SingleAudioPlaybackServiceArgs) {
		this.data = _args.track;
		this.store = _args.store;

		const initialState = this.store.getState();

		this.preferredMimeType = initialState.preferredMimeType;
		this.isLooping = this.data?.isLooping;
		this.isSubTrack = _args.isSubTrack ?? false;
		this.updateAdapterState = _args.updateAdapterState ?? (() => {});
		this._url = `${this.data.audioUrlStub}.${this.preferredMimeType}`;

		// initialize the master and track volume
		this._masterVolume = initialState.masterVolume;
		this._trackVolume = _args.trackVolume ?? 1;

		// html audio element setup
		this.audio = new Audio();
		this.audio.crossOrigin = "anonymous";
		this.audio.preload = "auto";
		this.audio.loop = this.isLooping;
		this.audio.volume = this._masterVolume * this._trackVolume;

		// add a custom listener to the playbackStore to update the volume when it changes
		this.store.subscribe(
			(state) => state.masterVolume,
			(newMasterVolume) => {
				this._masterVolume = newMasterVolume;
				this.audio.volume = this._masterVolume * this._trackVolume;
			},
		);

		// Attached custom callbacks to event listeners
		this.audio.onplay = () => {
			this.onPlay?.();
		};

		this.audio.onpause = () => {
			this.onPause?.();
		};

		this.audio.onended = () => {
			// if it ended then its essentially paused
			// set the internal status to paused
			this._isPlaying = false;
			if (!this.isSubTrack) {
				// notify the global state that the track has ended
				this.store.getState().setCurrentTrackStatus("ENDED");
			} else {
				this.updateAdapterState(this.data.id, "ENDED");
			}
			this.onEnded?.();
		};

		this.audio.onerror = () => {
			// if it ended then its paused
			// set the internal status to paused
			this._isPlaying = false;
			if (!this.isSubTrack) {
				// notify the global state that the track is paused
				this.store.getState().setCurrentTrackStatus("ERROR");
			} else {
				this.updateAdapterState(this.data.id, "ERROR");
			}

			// log the error to sentry
			const error = this.audio.error!;
			const errorMap = {
				1: "MEDIA_ERR_ABORTED",
				2: "MEDIA_ERR_NETWORK",
				3: "MEDIA_ERR_DECODE",
				4: "MEDIA_ERR_SRC_NOT_SUPPORTED",
			};
			const message =
				errorMap[error.code as keyof typeof errorMap] || "UNKNOWN_MEDIA_ERROR";
			console.error(`[Audio Error] ${message} (${error.code})`, error);
			Sentry.captureMessage(`Audio playback error: ${message}`, {
				level: "error",
				extra: {
					code: error.code,
					currentSrc: this.audio.currentSrc,
				},
			});
		};
	}

	async load(): Promise<void> {
		this.audio.src = this._url;
		this.audio.load();
	}

	async play(): Promise<void> {
		try {
			// Update the state so user see a quick updates
			// Also! Only set this as the current track if this is not a sub-track
			// the blended adapter will handle setting the current track otherwise
			if (!this.isSubTrack) {
				// not a sub track - set as the current track
				if (this.store.getState().currentTrack?.id !== this.data.id) {
					this.store.getState().setCurrentTrack(this.data);
				}
			} else {
				// this is a sub-track. Lets set loading state before we try and play
				// it so that the UI can update
				this.updateAdapterState(this.data.id, "LOADING");
			}

			// now try to play it
			await this.audio.play();

			// update the internal state status to playing
			this._isPlaying = true;

			if (!this.isSubTrack) {
				// notify the global state that the track is paused
				this.store.getState().setCurrentTrackStatus("PLAYING");
			} else {
				this.updateAdapterState(this.data.id, "PLAYING");
			}
		} catch (error) {
			this._isPlaying = false;

			if (!this.isSubTrack) {
				// notify the global state that the track is paused
				this.store.getState().setCurrentTrackStatus("PAUSED");
			} else {
				this.updateAdapterState(this.data.id, "PAUSED");
			}

			Sentry.captureException(error, {
				tags: {
					audioSource: this.audio.src,
				},
			});

			return Promise.resolve();
		}
	}

	async pause(): Promise<void> {
		try {
			// update the internal state status to paused
			this._isPlaying = false;

			if (!this.isSubTrack) {
				// notify the global state that the track is paused
				this.store.getState().setCurrentTrackStatus("PAUSED");
			} else {
				this.updateAdapterState(this.data.id, "PAUSED");
			}

			// pause the audio
			// we do this last because the pause event isn't instantaneous and we want the rest of
			// the code to react immediately to this event
			this.audio.pause();
		} catch (error) {
			Sentry.captureException(error, {
				tags: {
					audioSource: this.audio.src,
				},
			});
			return Promise.reject(error);
		}
	}

	async stop(): Promise<void> {
		if (!this._isPlaying) return;
		this.audio.pause();
		this.audio.currentTime = 0;
	}

	setLoop(loop: boolean): void {
		this.isLooping = loop;
		this.audio.loop = loop;
	}

	setTrackVolume(volume: number): void {
		// only set this if the track is a sub-track
		if (this.isSubTrack) {
			this._trackVolume = volume;
			this.audio.volume = this._masterVolume * this._trackVolume;
			this.updateAdapterState(this.data.id, undefined, this._trackVolume);
		}
	}

	isPlaying(): boolean {
		return this._isPlaying;
	}

	cleanup(): void {
		return;
	}

	status(): AdapterStatus {
		return {
			trackId: this.data.id,
			status: this._isPlaying ? "PLAYING" : "PAUSED",
			error: this.audio.error,
			trackVolume: this._trackVolume,
			blendStatus: [],
		};
	}
}
